import { Component, Input, ViewChild } from '@angular/core';
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { TranslocoPipe } from '@jsverse/transloco';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { LocationService } from '../../../../core/api/location.service';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { BaseLocation, Location } from '../../../../core/interfaces/location';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { DeleteConfirmationDialogComponent } from "../../../../shared/dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component";
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-create-company-location',
  standalone: true,
  imports: [LabeledInputComponent, TranslocoPipe, CustomButtonComponent, DeleteConfirmationDialogComponent, CommonModule, DialogModule],
  templateUrl: './create-company-location.component.html',
  styleUrl: './create-company-location.component.css'
})
export class CreateCompanyLocationComponent {
  constructor(private apiError: ApiErrorService, private locationService: LocationService, private commonValidator: CommonValidatorService) { }
  @Input() companyId: string | null = null;

  visible: boolean = false;
  isOpen: boolean = false;

  // Button click
  confirmClicked: boolean = false;

  // Location inputs
  streetInput: string = '';
  streetInputError: string | null = null;

  streetNumberInput: string = '';
  streetNumberInputError: string | null = null;

  postalCodeInput: string = '';
  postalCodeInputError: string | null = null;

  cityInput: string = '';
  cityInputError: string | null = null;

  provinceInput: string = '';
  provinceInputError: string | null = null;

  // Edit Location
  editLocation?: Location;

  // Dialog error
  dialogErrorMessage: string | null = null;

  // Successfuly created location
  @Input() successCallbackFunction!: () => void;

  // Toast
  @Input() toast?: ToastComponent;

  @ViewChild('createLocationDialog') confirmCreateLocation!: DeleteConfirmationDialogComponent;

  openModal(editLocation?: Location): void {
    this.visible = true;

    this.resetDialogData();

    if (editLocation !== undefined) {
      this.editLocation = editLocation;

      this.populateInputsFromLocation(editLocation);
    }
  }

  closeModal(): void {
    this.visible = false;
  }

  resetDialogData() {
    this.dialogErrorMessage = null;
    this.editLocation = undefined;
    this.resetInputData();
  }

  populateInputsFromLocation(editLocation: Location | null): void {
    if (!editLocation) {
      return;
    }

    this.streetInput = editLocation.street ?? '';
    this.streetNumberInput = editLocation.number ?? '';
    this.postalCodeInput = editLocation.postal_code ?? '';
    this.cityInput = editLocation.city ?? '';
    this.provinceInput = editLocation.province ?? '';
  }

  validateInputs(): boolean {
    const streetInputValue = this.streetInput.trim();
    const streetNumberInputValue = this.streetNumberInput.trim();
    const postalCodeInputValue = this.postalCodeInput.trim();
    const cityInputValue = this.cityInput.trim();
    const provinceInputValue = this.provinceInput.trim();

    // Validate length for text inputs
    const streetError = this.commonValidator.validateLength(streetInputValue, 2, 255);
    const streetNumberError = this.commonValidator.validateLength(streetNumberInputValue, 1, 10);
    const postalCodeError = this.commonValidator.validateLength(postalCodeInputValue, 1, 10);
    const cityError = this.commonValidator.validateLength(cityInputValue, 2, 255);
    const provinceError = this.commonValidator.validateLength(provinceInputValue, 2, 255);

    // Show errors
    this.streetInputError = streetError;
    this.streetNumberInputError = streetNumberError;
    this.postalCodeInputError = postalCodeError;
    this.cityInputError = cityError;
    this.provinceInputError = provinceError;

    // If there are any errors show them and prevent submission
    if (
      streetError || streetNumberError || postalCodeError || cityError || provinceError
    ) {
      return false;
    }

    return true;
  }

  populateLocation(id: string, is_active: boolean = false): Location {
    const newLocation: BaseLocation = {
      id: id,
      title: null,
      street: this.streetInput.trim(),
      number: this.streetNumberInput.trim(),
      postal_code: this.postalCodeInput.trim(),
      city: this.cityInput.trim(),
      province: this.provinceInput.trim(),
      latitude: null,
      longitude: null,
      is_active: is_active
    };
    return newLocation;
  }

  resetInputData() {
    this.streetInput = this.streetNumberInput = this.postalCodeInput =
      this.cityInput = this.provinceInput = '';

    this.streetInputError = this.streetNumberInputError = this.postalCodeInputError =
      this.cityInputError = this.provinceInputError = null;
    this.confirmClicked = false;
  }

  async confirmButtonClick() {
    if (this.confirmClicked || !this.companyId) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    var dialogResponse = await this.confirmCreateLocation.openModal();
    if (!dialogResponse) {
      this.confirmClicked = false;
      return;
    }

    // Create location
    var responseNewLocation: BaseLocation | undefined;

    // Send request for creating location
    try {
      if (this.editLocation === undefined) {
        // New Location
        const newLocation: Location = this.populateLocation('');
        responseNewLocation = await firstValueFrom(this.locationService.createCompanyLocation(this.companyId, newLocation));
      }
      else {
        // Edit Location
        const newLocation: Location = this.populateLocation(this.editLocation.id, this.editLocation.is_active);
        responseNewLocation = await firstValueFrom(this.locationService.updateLocation(newLocation));
      }

      this.toast?.showToast('shared.success', 'admin.create_location_success', 'success', 5000, true);
      this.resetInputData();

      if (this.successCallbackFunction) {
        this.successCallbackFunction();
      }

      this.closeModal();
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }

      this.dialogErrorMessage = await this.apiError.getErrorMessage(crid);
    }

    this.confirmClicked = false;
  }
}
